import React, { useRef, useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Toolbar,
  Button,
  Box,
  InputBase,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
  Badge,
  Container,
  Fade,
  Stack,
  IconButton,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import MobileDropdownContent from "./mobile-dropdown-content";
import DesktopDropdownContent from "./desktop-dropdown-content";
import { mediaUrl, mediaSrcSet } from "../../../../utils/cfImage";

const MyBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    color: "#fff",
    backgroundColor: "#000",
    top: -2,
    right: -2,
    fontSize: "10px",
    p: 0,
  },
}));

const MyIconButton = styled(IconButton)(({ theme }) => ({
  minWidth: "unset",
  fontSize: 16,
  color: "#fff",
  padding: "6px",
  [theme.breakpoints.up("lg")]: {
    fontSize: 20,
    padding: "8px",
  },
  "&:hover": {
    backgroundColor: "transparent",
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  flexGrow: 1,
  "& .MuiInputBase-input": {
    padding: "4px 4px 4px 0px",
    color: "#000",
    transition: theme.transitions.create("width"),
    width: "100%",
    // [theme.breakpoints.up("md")]: {
    //   width: "20ch",
    // },
  },
}));

export default function MainAppbar(props) {
  const { component } = props;
  const { openMenu, setOpenMenu } = component.props;

  const {
    wishlist = [],
    order = null,
    mainMenu = [],
    coupons = [],
  } = component.getData("default", {});

  const isAuth = component.isAuthenticated();

  const theme = useTheme();

  const devHelper = component.getHelpers("dev");
  const filesUrl = component.getHelpers("env").get("files_url");
  const imgVersion = component.getHelpers("value").getValue("images-version");

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [searchForm, setSearchForm] = useState(false);
  const inputRef = useRef(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);

  const page = window.location.pathname;

  const [menu, setMenu] = useState("");

  const [menuEffect, setMenuEffect] = useState(false);

  const [womensImage, setWomensImage] = useState("women-rings");
  const [mensImage, setMensImage] = useState("men-rings");

  const [imageSpeed, setImageSpeed] = useState(0);

  const womens = mainMenu.find((menu) => menu.slug === "womens");
  const mens = mainMenu.find((menu) => menu.slug === "mens");
  const gifts = mainMenu.find((menu) => menu.slug === "gifts");
  const collections = mainMenu.find((menu) => menu.slug === "collections");

  const navMenu = mainMenu;

  const handleToggle = (boolean, new_menu) => {
    if (!boolean && isScrollingRef.current) {
      return;
    }

    setOpenMenu(boolean);
    setWomensImage("women-rings");
    setMensImage("men-rings");
    setSearchForm(false);
    setImageSpeed(0);
    if (new_menu !== undefined) {
      setMenu(new_menu);
    }
  };

  const handleToggleAccountInfo = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  useEffect(() => {
    if (navMenu.length) {
      setTimeout(() => {
        setMenuEffect(true);
      }, 500);
    }
  }, [navMenu.length]);

  useEffect(() => {
    if (!openMenu) {
      isScrollingRef.current = false;
      return;
    }

    const onScroll = () => {
      isScrollingRef.current = true;
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 200);
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(scrollTimeoutRef.current);
    };
  }, [openMenu]);

  useEffect(() => {
    if (navMenu.length && menu && !navMenu.find((item) => item.slug === menu)) {
      setMenu(navMenu[0].slug);
    }
  }, [navMenu, menu]);

  return (
    <>
      {/* Top navigation bar: left menu trigger/links, centered logo, right user actions */}
      <Toolbar disableGutters sx={{ bgcolor: "#b6b7ae" }}>
        <Container maxWidth="xl" disableGutters>
          <Stack
            direction="row"
            sx={{
              px: { xs: 2, lg: 4 },
              justifyContent: "space-between",
              alignItems: "center",
              height: {
                xs: theme.appbar_height.mobile,
                lg: theme.appbar_height.desktop,
              },
            }}
          >
            <Box
              sx={{
                width: { xs: 132, lg: 400 },
                height: "100%",
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
              }}
            >
              <Stack
                direction="row"
                spacing={0}
                sx={{
                  display: { xs: "none", lg: "flex" },
                  justifyContent: "flex-start",
                  alignItems: "stretch",
                  height: "100%",
                }}
              >
                {navMenu.map((menu, index) => {
                  const { slug, name, url } = menu;
                  return (
                    <Box
                      key={`menu-btn-${slug}`}
                      onMouseEnter={() => {
                        if (menuEffect || openMenu) {
                          handleToggle(true, slug);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!openMenu) {
                          handleToggle(false, slug);
                        }
                      }}
                      onClick={() => handleToggle(false, slug)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pl: index === 0 ? 0 : 1.5,
                        pr: index === navMenu.length - 1 ? 0 : 1.5,
                        cursor: "pointer",
                        "&:hover,&:focus": {
                          "& .main-menu-link-label": {
                            borderBottom: "2px solid #fff",
                          },
                        },
                      }}
                    >
                      <Button
                        to={url}
                        component={RouterLink}
                        key={`main-menu-root-${slug}`}
                        sx={{
                          color: "#fff",
                          textTransform: "none",
                          fontSize: 14,
                          lineHeight: 1,
                          py: 4,
                          px: 0.5,
                          minWidth: 0,
                          minHeight: 44,
                          fontFamily: "Spartan-Bold",
                          borderRadius: 0,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          "&:hover,&:focus": {
                            backgroundColor: "transparent",
                            "& .main-menu-link-label": {
                              borderBottom: "2px solid #fff",
                            },
                          },
                        }}
                        disableRipple
                      >
                        <Box
                          component="span"
                          className="main-menu-link-label"
                          sx={{
                            lineHeight: 1,
                            borderBottom:
                              page === url
                                ? "2px solid #fff"
                                : "2px solid transparent",
                          }}
                        >
                          {name}
                        </Box>
                      </Button>
                    </Box>
                  );
                })}
              </Stack>
              <Button
                disableRipple
                onClick={() => {
                  setOpenMenu(!openMenu);
                }}
                aria-label="Mobile Menu"
                sx={{
                  display: { xs: "inline-flex", lg: "none" },
                  color: "#fff",
                  fontSize: 13,
                  lineHeight: 1,
                  p: 0,
                  minWidth: 0,
                  fontFamily: "Spartan-Bold",
                  borderRadius: 0,
                  "&:hover,&:focus": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                MENU
              </Button>
            </Box>
            <Button
              sx={{
                outline: "none",
                p: 0,
                "&:hover": {
                  bgcolor: "transparent",
                },
                img: { width: { xs: 60, sm: 75 } },
              }}
              disableRipple
              component={RouterLink}
              to="/"
            >
              <img
                src={mediaUrl(filesUrl, "logos/white/square.png", {
                  width: 350,
                  version: imgVersion,
                })}
                srcSet={mediaSrcSet(
                  filesUrl,
                  "logos/white/square.png",
                  [
                    { width: 300, w: 600 },
                    { width: 350, w: 900 },
                  ],
                  { version: imgVersion },
                )}
                sizes="(max-width: 600px) 300px, (max-width: 900px) 350px"
                alt="Konstantino Home"
                title="Konstantino Home"
              />
            </Button>
            <Stack
              direction="row"
              spacing={{ xs: 0.25, lg: 1 }}
              sx={{
                justifyContent: "flex-end",
                alignItems: "center",
                width: { xs: 132, lg: 400 },
                height: "100%",
              }}
            >
              <MyIconButton
                disableRipple
                onClick={() => {
                  setSearchForm(true);
                }}
                aria-label="Search"
              >
                <i className="fa-regular fa-magnifying-glass" />
              </MyIconButton>
              {!isAuth ? (
                <MyIconButton
                  component={RouterLink}
                  to="/signin"
                  disableRipple
                  aria-label="User Account"
                >
                  <i className="fa-regular fa-user" />
                </MyIconButton>
              ) : (
                <>
                  <MyIconButton
                    ref={anchorRef}
                    aria-controls={open ? "menu-list-grow" : undefined}
                    aria-haspopup="true"
                    onClick={handleToggleAccountInfo}
                    disableRipple
                    aria-label="User Account"
                  >
                    <MyBadge badgeContent={coupons.length}>
                      <i className="fa-regular fa-user" />
                    </MyBadge>
                  </MyIconButton>
                  <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    transition
                  >
                    {({ TransitionProps, placement }) => (
                      <Grow
                        {...TransitionProps}
                        style={{
                          transformOrigin:
                            placement === "bottom"
                              ? "center top"
                              : "center bottom",
                        }}
                      >
                        <Paper square>
                          <ClickAwayListener onClickAway={handleClose}>
                            <MenuList
                              autoFocusItem={open}
                              id="menu-list-grow"
                              onKeyDown={handleListKeyDown}
                            >
                              <MenuItem
                                component={RouterLink}
                                to="/profile"
                                onClick={handleClose}
                                sx={{
                                  fontFamily:
                                    "founders-grotesk,Helvetica,sans-serif",
                                  zIndex: 1000000,
                                }}
                              >
                                {component.ucfirst("k-profile")}
                              </MenuItem>
                              <MenuItem
                                component={RouterLink}
                                to="/addresses"
                                onClick={handleClose}
                                sx={{
                                  fontFamily:
                                    "founders-grotesk,Helvetica,sans-serif",
                                }}
                              >
                                {component.ucfirst("k-addresses")}
                              </MenuItem>
                              <MenuItem
                                component={RouterLink}
                                to="/orders"
                                onClick={handleClose}
                                sx={{
                                  fontFamily:
                                    "founders-grotesk,Helvetica,sans-serif",
                                }}
                              >
                                {component.ucfirst("k-orders")}
                              </MenuItem>
                              <MenuItem
                                component={RouterLink}
                                to="/coupons"
                                onClick={handleClose}
                                sx={{
                                  fontFamily:
                                    "founders-grotesk,Helvetica,sans-serif",
                                }}
                              >
                                <MyBadge
                                  badgeContent={coupons.length}
                                  sx={{
                                    "& .MuiBadge-badge": {
                                      right: -15,
                                      top: 12,
                                      background: "#000",
                                      color: "#fff",
                                    },
                                  }}
                                >
                                  {component.ucfirst("k-coupons")}
                                </MyBadge>
                              </MenuItem>
                              <MenuItem
                                onClick={() => component.getPage().logout()}
                                sx={{
                                  fontFamily:
                                    "founders-grotesk,Helvetica,sans-serif",
                                }}
                              >
                                {component.ucfirst("logout")}
                              </MenuItem>
                            </MenuList>
                          </ClickAwayListener>
                        </Paper>
                      </Grow>
                    )}
                  </Popper>
                </>
              )}
              <MyIconButton
                disableRipple
                component={RouterLink}
                to="/wishlist"
                aria-label="Wishlist"
              >
                <MyBadge badgeContent={wishlist.length}>
                  <i className="fa-regular fa-heart" />
                </MyBadge>
              </MyIconButton>
              <MyIconButton
                disableRipple
                component={RouterLink}
                to="/cart"
                onMouseEnter={() => component.toggleCartPopper(true)}
                onClick={() => component.toggleCartPopper(true)}
                aria-label="Cart"
              >
                <MyBadge
                  badgeContent={devHelper.getObjectValue(
                    order,
                    "amounts.items",
                    0,
                  )}
                >
                  <i className="fa-regular fa-bag-shopping" />
                </MyBadge>
              </MyIconButton>
            </Stack>
          </Stack>
        </Container>
      </Toolbar>
      {/* Global search quick panel */}
      {searchForm && (
        <ClickAwayListener onClickAway={() => setSearchForm(false)}>
          <Container
            maxWidth="xl"
            disableGutters
            sx={{ bgcolor: { xs: "#fff", lg: "transparent" } }}
          >
            <Stack
              direction="row"
              sx={{
                bgcolor: "transparent",
                justifyContent: { xs: "center", lg: "flex-end" },
              }}
            >
              <Box
                sx={{ bgcolor: "#fff", p: 1, width: { xs: "100%", lg: 500 } }}
              >
                <Box
                  component="form"
                  onSubmit={(e) => component.checkSearch(e)}
                  method="get"
                  sx={{
                    position: "relative",
                    bgcolor: "transparent",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      sx={{
                        fontSize: "16px",
                        color: "#000",
                      }}
                      aria-label="Close Search"
                      onClick={() => {
                        setSearchForm(false);
                      }}
                    >
                      <i className="fa-sharp fa-solid fa-xmark-large" />
                    </IconButton>
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: "flex-start",
                        alignItems: "center",
                        flexGrow: 1,
                        border: "1px solid #000",
                        p: 0.5,
                      }}
                    >
                      <IconButton
                        type="submit"
                        sx={{
                          fontSize: "16px",
                          color: "#000",
                        }}
                        disableRipple
                        aria-label="Search"
                      >
                        <i className="fa-solid fa-magnifying-glass" />
                      </IconButton>
                      <StyledInputBase
                        placeholder="Search…"
                        value={component.getData("default.search_term", "")}
                        onChange={(e) => {
                          component.setData({
                            "default.search_term": e.target.value,
                          });
                        }}
                        autoFocus
                        inputRef={inputRef}
                        inputProps={{ "aria-label": "search" }}
                      />
                    </Stack>
                    <Button
                      type="submit"
                      disableRipple
                      sx={{
                        color: "#fff",
                        borderRadius: 0,
                        fontSize: 12,

                        height: 42,
                      }}
                      variant="contained"
                      size="small"
                    >
                      Explore
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Container>
        </ClickAwayListener>
      )}
      <Fade in={openMenu} timeout={{ enter: 500, exit: 0 }}>
        <Box>
          {/* Mobile dropdown menu content */}
          <MobileDropdownContent
            menu={menu}
            navMenu={navMenu}
            setMenu={setMenu}
            handleToggle={handleToggle}
            devHelper={devHelper}
            womens={womens}
            mens={mens}
            gifts={gifts}
            collections={collections}
            filesUrl={filesUrl}
            imgVersion={imgVersion}
            component={component}
            isAuth={isAuth}
          />
          {/* Desktop mega menu content */}
          <DesktopDropdownContent
            menu={menu}
            handleToggle={handleToggle}
            devHelper={devHelper}
            womens={womens}
            mens={mens}
            gifts={gifts}
            collections={collections}
            womensImage={womensImage}
            setWomensImage={setWomensImage}
            mensImage={mensImage}
            setMensImage={setMensImage}
            imageSpeed={imageSpeed}
            setImageSpeed={setImageSpeed}
            filesUrl={filesUrl}
            imgVersion={imgVersion}
          />
        </Box>
      </Fade>
    </>
  );
}
